// =============================================================================
// BP-05-03: Calendar Frontend - CalendarExportModal Component
// =============================================================================

import React, { useState } from 'react';
import { format, subMonths, addMonths } from 'date-fns';
import { Download, X, Calendar, FileText, File } from 'lucide-react';
import { type CalendarExportOptions, type CalendarDateRange } from '../types';
import { useExportCalendar } from '../hooks';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface CalendarExportModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
  children?: Array<{ id: string; name: string }>;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function CalendarExportModal({
  familyId,
  isOpen,
  onClose,
  children = [],
}: CalendarExportModalProps) {
  const exportMutation = useExportCalendar();

  const [format_, setFormat] = useState<'ical' | 'csv'>('ical');
  const [dateRange, setDateRange] = useState<'month' | '3months' | '6months' | 'year' | 'custom'>(
    '3months'
  );
  const [customStart, setCustomStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState(format(addMonths(new Date(), 3), 'yyyy-MM-dd'));
  const [selectedChildId, setSelectedChildId] = useState<string>('');
  const [includeNotes, setIncludeNotes] = useState(true);

  if (!isOpen) return null;

  const getDateRange = (): CalendarDateRange => {
    const now = new Date();
    switch (dateRange) {
      case 'month':
        return {
          start: subMonths(now, 1).toISOString(),
          end: addMonths(now, 1).toISOString(),
        };
      case '3months':
        return {
          start: subMonths(now, 1).toISOString(),
          end: addMonths(now, 3).toISOString(),
        };
      case '6months':
        return {
          start: subMonths(now, 1).toISOString(),
          end: addMonths(now, 6).toISOString(),
        };
      case 'year':
        return {
          start: subMonths(now, 1).toISOString(),
          end: addMonths(now, 12).toISOString(),
        };
      case 'custom':
        return {
          start: new Date(customStart).toISOString(),
          end: new Date(customEnd).toISOString(),
        };
      default:
        return {
          start: now.toISOString(),
          end: addMonths(now, 3).toISOString(),
        };
    }
  };

  const handleExport = async () => {
    const options: CalendarExportOptions = {
      format: format_,
      dateRange: getDateRange(),
      childId: selectedChildId || undefined,
      includeNotes,
    };

    try {
      await exportMutation.mutateAsync({ familyId, options });
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-lifted w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-gray/10">
          <h2 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
            <Download className="w-5 h-5 text-sage" />
            Export Calendar
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-cream rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-warm-gray" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Export Format
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormat('ical')}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border-2 transition-colors',
                  format_ === 'ical'
                    ? 'border-sage bg-sage/5'
                    : 'border-warm-gray/20 hover:border-warm-gray/40'
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    format_ === 'ical' ? 'bg-sage/20' : 'bg-cream'
                  )}
                >
                  <Calendar
                    className={cn(
                      'w-5 h-5',
                      format_ === 'ical' ? 'text-sage' : 'text-warm-gray'
                    )}
                  />
                </div>
                <div className="text-left">
                  <p className="font-medium text-navy">iCalendar</p>
                  <p className="text-xs text-warm-gray">.ics file</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormat('csv')}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border-2 transition-colors',
                  format_ === 'csv'
                    ? 'border-sage bg-sage/5'
                    : 'border-warm-gray/20 hover:border-warm-gray/40'
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    format_ === 'csv' ? 'bg-sage/20' : 'bg-cream'
                  )}
                >
                  <FileText
                    className={cn(
                      'w-5 h-5',
                      format_ === 'csv' ? 'text-sage' : 'text-warm-gray'
                    )}
                  />
                </div>
                <div className="text-left">
                  <p className="font-medium text-navy">Spreadsheet</p>
                  <p className="text-xs text-warm-gray">.csv file</p>
                </div>
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
              className="w-full px-4 py-2.5 bg-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
            >
              <option value="month">Past & Next Month</option>
              <option value="3months">Next 3 Months</option>
              <option value="6months">Next 6 Months</option>
              <option value="year">Next Year</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-warm-gray mb-1">Start</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-3 py-2 bg-cream rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage/20"
                  />
                </div>
                <div>
                  <label className="block text-xs text-warm-gray mb-1">End</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    min={customStart}
                    className="w-full px-3 py-2 bg-cream rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage/20"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Child Filter */}
          {children.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-navy mb-2">
                Filter by Child
              </label>
              <select
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                className="w-full px-4 py-2.5 bg-cream rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/20"
              >
                <option value="">All Children</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Options */}
          {format_ === 'csv' && (
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeNotes}
                  onChange={(e) => setIncludeNotes(e.target.checked)}
                  className="rounded border-warm-gray text-sage focus:ring-sage"
                />
                <span className="text-sm text-navy">Include notes in export</span>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-cream/50 border-t border-warm-gray/10 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-navy font-medium rounded-lg hover:bg-cream transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sage text-white font-medium rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exportMutation.isPending ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CalendarExportModal;
